/**
 * Invoice line-item kinds (invoicing-ops Phase 4).
 *
 * Backed by invoice_items.kind (VarChar(20), default FEE), deliberately not a
 * Prisma enum so values ship as shared constants like the approval statuses.
 * FEE is every ordinary billed line (schedule lines, time, retainer fees);
 * REIMBURSEMENT lines are built from expenses and carry expenseId when they
 * map to a single receipt. ADVANCE, ADJUSTMENT, SURCHARGE, and LATE_FEE are
 * reserved for Phase 5 anatomy work (sections, CC surcharge, late-fee assist)
 * and are valid to store now.
 */
export const INVOICE_LINE_KINDS = {
  FEE: 'FEE',
  REIMBURSEMENT: 'REIMBURSEMENT',
  ADVANCE: 'ADVANCE',
  ADJUSTMENT: 'ADJUSTMENT',
  SURCHARGE: 'SURCHARGE',
  LATE_FEE: 'LATE_FEE',
} as const

export type InvoiceLineKind =
  (typeof INVOICE_LINE_KINDS)[keyof typeof INVOICE_LINE_KINDS]

export const INVOICE_LINE_KIND_VALUES = Object.values(
  INVOICE_LINE_KINDS,
) as InvoiceLineKind[]

export function isInvoiceLineKind(value: unknown): value is InvoiceLineKind {
  return (
    typeof value === 'string' &&
    (INVOICE_LINE_KIND_VALUES as string[]).includes(value)
  )
}

/**
 * Document section order and headings for grouped line-item rendering
 * (invoicing-ops Phase 5). Both the PDF and the web paper consume this spec
 * so the two documents can never disagree on ordering or copy.
 */
export const INVOICE_LINE_KIND_SECTION_ORDER: InvoiceLineKind[] = [
  INVOICE_LINE_KINDS.FEE,
  INVOICE_LINE_KINDS.REIMBURSEMENT,
  INVOICE_LINE_KINDS.ADVANCE,
  INVOICE_LINE_KINDS.ADJUSTMENT,
  INVOICE_LINE_KINDS.SURCHARGE,
  INVOICE_LINE_KINDS.LATE_FEE,
]

export const INVOICE_LINE_KIND_SECTION_LABELS: Record<InvoiceLineKind, string> =
  {
    FEE: 'Fees',
    REIMBURSEMENT: 'Reimbursements',
    ADVANCE: 'Advances',
    ADJUSTMENT: 'Adjustments',
    SURCHARGE: 'Surcharges',
    LATE_FEE: 'Late fees',
  }

export interface InvoiceLineKindSection<T> {
  kind: InvoiceLineKind
  label: string
  items: T[]
}

export type GroupedInvoiceLineItems<T> =
  | { grouped: false }
  | { grouped: true; sections: InvoiceLineKindSection<T>[] }

/**
 * Group line items into document sections by kind. An absent or invalid kind
 * counts as FEE, matching the invoice_items column default and the Phase 4
 * backfill, so every pre-Phase-4 invoice (all rows FEE) takes the
 * `grouped: false` branch and renders exactly as it always has. Input order
 * is preserved inside each section.
 */
export function groupInvoiceLineItemsByKind<
  T extends { kind?: string | null },
>(items: T[]): GroupedInvoiceLineItems<T> {
  const kindOf = (item: T): InvoiceLineKind =>
    isInvoiceLineKind(item.kind) ? item.kind : INVOICE_LINE_KINDS.FEE
  if (items.every(item => kindOf(item) === INVOICE_LINE_KINDS.FEE)) {
    return { grouped: false }
  }
  const sections = INVOICE_LINE_KIND_SECTION_ORDER.map(kind => ({
    kind,
    label: INVOICE_LINE_KIND_SECTION_LABELS[kind],
    items: items.filter(item => kindOf(item) === kind),
  })).filter(section => section.items.length > 0)
  return { grouped: true, sections }
}
